import { Apollo } from 'apollo-angular'
import { HttpLink } from 'apollo-angular/http'
import { ApolloLink, InMemoryCache } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { NgModule } from '@angular/core'
import { CommonModule } from '@angular/common'

import { environment } from 'src/environments/environment'

import { AuthService } from '../auth/auth.service'
import { DevTenantService } from '../core/dev-tenant.service'
import { createTrackingLink } from '../core/errors/trackingLink'

function getHasuraUri() {
  const captureClientNameFromHostRegex = /^(.*)\.myaqs\.com/m
  const { hostname } = window.location
  const clientName =
    captureClientNameFromHostRegex.exec(hostname)?.[1] || 'dev2'
  // Multi-tenant parity period: every `<shortname>-mt.myaqs.com` subdomain
  // (and the bare `mt.myaqs.com`) shares one Hasura at
  // `mt-hasura.myaqs.com`. The server resolves the tenant from the JWT's
  // `clientId` claim, which Hasura forwards via "forward all headers from
  // client" on the remote schema. After cutover, `<shortname>.myaqs.com`
  // hostnames flip to also resolve here.
  if (clientName === 'mt' || clientName.endsWith('-mt')) {
    return `https://mt-hasura.myaqs.com/v1/graphql`
  }
  return `https://${clientName}-hasura.myaqs.com/v1/graphql`
}

const hasuraEndpoint = environment.hasuraUri || getHasuraUri()

@NgModule({
  declarations: [],
  imports: [CommonModule],
})
export class HasuraModule {
  constructor(
    private apollo: Apollo,
    private httpLink: HttpLink,
    private auth: AuthService,
    private devTenant: DevTenantService,
  ) {
    const hasuraHttpLink = httpLink.create({ uri: hasuraEndpoint })
    const hasuraLink = ApolloLink.from([hasuraHttpLink])
    // Every app action flows through this client, so piggyback a throttled
    // "is my account still active" self-check on each operation (except the
    // check itself, to avoid recursion). Ejects soft-deleted users.
    const statusCheckLink = new ApolloLink((operation, forward) => {
      if (operation.operationName !== 'selfStatus') {
        auth.checkAccountActive()
      }
      return forward(operation)
    })
    const authLink = setContext((_, { headers }) => {
      // get the authentication token from local storage if it exists
      const token = auth.token
      const tenant = devTenant.current()
      // return the headers to the context so httpLink can read them
      return {
        headers: {
          ...headers,
          Authorization: token ? `Bearer ${token}` : '',
          ...(tenant ? { 'X-Dev-Tenant': tenant } : {}),
        },
      }
    })
    apollo.createDefault({
      // the tracking link first: it sees every operation as the app sent it
      // and every result as the app receives it
      link: ApolloLink.from([
        createTrackingLink(),
        statusCheckLink,
        authLink,
        hasuraLink,
      ]),
      cache: new InMemoryCache(),
      defaultOptions: {
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
      },
    })
  }
}
